import Container from "@/components/container";
import { SectionHeading } from "@/components/marketing/section-heading";
import { cn } from "@/lib/utils";
import Image from "next/image";
import type { ReactNode } from "react";
import {
  ProductBadge,
  ProductHeroActions,
  ProductRow,
} from "./product-blocks";

export function SplitHero({
  badge,
  title,
  subtitle,
  media,
}: {
  badge: string;
  title: ReactNode;
  subtitle: ReactNode;
  media: ReactNode;
}) {
  return (
    <ProductRow>
      <div className="grid grid-cols-1 items-center gap-12 pt-16 pb-16 lg:grid-cols-12 lg:gap-10 lg:pt-24 lg:pb-24">
        <header className="flex flex-col items-start lg:col-span-5">
          <div className="mb-6">
            <ProductBadge>
              <h1>{badge}</h1>
            </ProductBadge>
          </div>
          <h2 className="text-balance text-4xl font-medium leading-[1.05] tracking-tight text-neutral-900 sm:text-5xl xl:text-6xl">
            {title}
          </h2>
          <p className="mt-5 max-w-xl text-pretty text-lg leading-relaxed text-neutral-600">
            {subtitle}
          </p>
          <ProductHeroActions className="items-start md:justify-start" />
        </header>
        <div className="min-w-0 lg:col-span-7">{media}</div>
      </div>
    </ProductRow>
  );
}

export function PhotoStage({
  src,
  alt,
  priority,
  className,
  imageClassName,
  children,
}: {
  src: string;
  alt: string;
  priority?: boolean;
  className?: string;
  imageClassName?: string;
  children?: ReactNode;
}) {
  return (
    <div
      className={cn(
        "relative isolate overflow-hidden rounded-2xl bg-neutral-100",
        className,
      )}
    >
      <Image
        src={src}
        alt={alt}
        fill
        unoptimized
        priority={priority}
        className={cn("-z-10 object-cover", imageClassName)}
      />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-gradient-to-t from-black/30 via-black/0 to-black/0"
      />
      {children}
    </div>
  );
}

export function FloatingCard({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "rounded-xl border border-neutral-200/80 bg-white p-3 shadow-[0_16px_40px_-20px_rgba(15,23,42,0.45)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function ShowcaseSection({
  id,
  badge,
  title,
  subtitle,
  className,
  children,
}: {
  id?: string;
  badge?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <ProductRow id={id}>
      <div className={cn("flex flex-col gap-10 py-16 lg:gap-12 lg:py-24", className)}>
        <SectionHeading
          badge={badge ? <ProductBadge>{badge}</ProductBadge> : undefined}
          title={title}
          subtitle={subtitle}
          breakBeforeSubtitle
        />
        {children}
      </div>
    </ProductRow>
  );
}

export function BentoGrid({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("grid grid-cols-1 gap-4 md:grid-cols-2", className)}>
      {children}
    </div>
  );
}

export function BentoCard({
  id,
  lead,
  text,
  wide,
  className,
  visualClassName,
  children,
}: {
  id?: string;
  lead: string;
  text: ReactNode;
  wide?: boolean;
  className?: string;
  visualClassName?: string;
  children: ReactNode;
}) {
  return (
    <article
      id={id}
      className={cn(
        "flex min-w-0 flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white",
        wide && "md:col-span-2",
        id && "scroll-mt-24",
        className,
      )}
    >
      <p className="max-w-[36rem] px-6 pt-6 text-[15px] leading-relaxed text-neutral-500 sm:px-8 sm:pt-8">
        <span className="font-medium text-neutral-900">{lead}</span> {text}
      </p>
      <div
        className={cn(
          "dot-grid relative mt-6 flex min-h-[300px] flex-1 items-end justify-center overflow-hidden border-t border-neutral-100 bg-neutral-50 px-6 pt-8 sm:px-8",
          visualClassName,
        )}
      >
        {children}
      </div>
    </article>
  );
}

export function CroppedSheet({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "w-full max-w-[420px] rounded-t-xl border border-b-0 border-neutral-200 bg-white p-4 pb-6 shadow-[0_16px_40px_-24px_rgba(15,23,42,0.35)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

export type Story = {
  src: string;
  alt: string;
  title: string;
  text: string;
};

function StoryCard({ story, className }: { story: Story; className?: string }) {
  return (
    <figure
      className={cn(
        "relative isolate flex min-h-[260px] flex-col justify-end overflow-hidden rounded-2xl bg-neutral-900 p-6 sm:p-8",
        className,
      )}
    >
      <Image
        src={story.src}
        alt={story.alt}
        fill
        unoptimized
        className="-z-10 object-cover"
      />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-gradient-to-t from-black/80 via-black/25 to-black/0"
      />
      <figcaption className="flex max-w-[26rem] flex-col gap-1.5">
        <span className="text-lg font-medium text-white">{story.title}</span>
        <span className="text-sm leading-relaxed text-white/75">
          {story.text}
        </span>
      </figcaption>
    </figure>
  );
}

export function StoryGrid({ stories }: { stories: [Story, Story, Story] }) {
  const [first, second, third] = stories;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:grid-rows-2">
      <StoryCard story={first} className="md:row-span-2 md:min-h-[540px]" />
      <StoryCard story={second} />
      <StoryCard story={third} />
    </div>
  );
}

export type DarkFeature = {
  title: string;
  text: string;
  visual: ReactNode;
};

export function DarkFeatureGrid({
  id,
  title,
  subtitle,
  items,
}: {
  id?: string;
  title: ReactNode;
  subtitle: ReactNode;
  items: DarkFeature[];
}) {
  return (
    <Container
      id={id}
      className={cn("border-neutral-900 bg-neutral-950", id && "scroll-mt-24")}
      containerClassName="border-neutral-900"
    >
      <div className="grid grid-cols-12">
        <div className="col-span-full flex flex-col gap-12 px-4 py-20 lg:col-[2/-2] lg:px-0 lg:py-28">
          <h2 className="max-w-[48rem] text-balance text-3xl font-medium leading-tight text-white md:text-5xl">
            <span className="text-neutral-500">{title}</span>{" "}
            <br className="hidden md:block" />
            <span>{subtitle}</span>
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => (
              <div
                key={item.title}
                className="flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]"
              >
                <div className="relative flex h-40 items-center justify-center px-6">
                  <div
                    aria-hidden
                    className="absolute inset-x-0 top-1/2 border-t border-dashed border-white/[0.08]"
                  />
                  <div
                    aria-hidden
                    className="absolute inset-y-0 left-1/2 border-l border-dashed border-white/[0.08]"
                  />
                  <div className="relative">{item.visual}</div>
                </div>
                <div className="flex flex-col gap-1.5 px-6 pb-6">
                  <h3 className="text-[17px] font-medium text-white">
                    {item.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-neutral-400">
                    {item.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Container>
  );
}

export function DarkChip({
  icon,
  active,
  children,
}: {
  icon?: ReactNode;
  active?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs [&_svg]:size-3.5",
        active
          ? "border-white/20 bg-white/10 text-white"
          : "border-white/10 bg-neutral-900 text-neutral-400",
      )}
    >
      {icon}
      {children}
    </span>
  );
}

export function QuoteBlock({ quote, name }: { quote: string; name: string }) {
  return (
    <ProductRow>
      <figure className="flex flex-col items-start gap-8 py-20 lg:py-28">
        <blockquote className="max-w-[52rem] text-balance text-3xl font-medium leading-tight tracking-tight text-neutral-900 md:text-4xl">
          &ldquo;{quote}&rdquo;
        </blockquote>
        <figcaption className="text-sm text-neutral-500">{name}</figcaption>
      </figure>
    </ProductRow>
  );
}
